import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { useToast } from '../Toast';
import {
  useMfaStatusQuery,
  useMfaSetupStartMutation,
  useMfaSetupVerifyMutation,
  useMfaDisableMutation,
} from '../../store/api/localApi';
import { ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';

type Stage = 'idle' | 'password' | 'confirm' | 'disable-confirm';

const MfaPanel: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { data: mfaStatus, isLoading: isStatusLoading } = useMfaStatusQuery();
  const [setupStart, { isLoading: isStarting }] = useMfaSetupStartMutation();
  const [setupVerify, { isLoading: isVerifying }] = useMfaSetupVerifyMutation();
  const [disableMfa, { isLoading: isDisabling }] = useMfaDisableMutation();

  const [stage, setStage] = useState<Stage>('idle');
  // Password lives in component state only for the enroll exchange — never
  // persisted to localStorage/redux. Both steps of enrollment re-verify it:
  // setup/start mints an access token from it, and setup/verify re-auths with
  // it again before enabling TOTP. So it must survive from the password step
  // through the confirm step, and is cleared only once the exchange fully
  // resolves (verify success/failure) or the flow is cancelled.
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setupData, setSetupData] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Render the otpauth URI as a scannable QR straight onto the canvas. This is
  // fully client-side (the qrcode lib does no network I/O), so the TOTP secret
  // never leaves the browser. Runs whenever a new setupData arrives.
  useEffect(() => {
    const canvas = qrCanvasRef.current;
    if (!canvas || !setupData) return;
    QRCode.toCanvas(canvas, setupData.otpauthUri, { width: 176, margin: 1 }, (err) => {
      // A QR render failure is non-fatal — the secret + otpauth link below are
      // the manual-entry fallback, so we just skip the image rather than block
      // enrollment.
      if (err) console.warn('MFA QR render failed; manual entry still available');
    });
  }, [setupData]);

  const resetFlow = () => {
    setStage('idle');
    setPassword('');
    setCode('');
    setSetupData(null);
  };

  const handleEnableClick = () => {
    setStage('password');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await setupStart({ password }).unwrap();
      setSetupData(result);
      setStage('confirm');
      // Keep `password` in state — setup/verify (the confirm step) re-auths
      // with it again. It is cleared once verify resolves, in
      // handleConfirmSubmit's finally, or on cancel via resetFlow.
    } catch (err) {
      const message =
        (err as { data?: { error?: string } })?.data?.error ??
        'Could not verify your password. Please try again.';
      showError(message);
      // On failure the flow doesn't advance, so drop the password now.
      setPassword('');
    }
  };

  const handleConfirmSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await setupVerify({ password, code }).unwrap();
      showSuccess('Two-factor authentication is now enabled');
      resetFlow();
    } catch (err) {
      const message =
        (err as { data?: { error?: string } })?.data?.error ??
        'Could not confirm that code. Please try again.';
      showError(message);
    } finally {
      setPassword('');
    }
  };

  const handleDisableClick = () => {
    setStage('disable-confirm');
  };

  const handleDisableConfirm = async () => {
    try {
      await disableMfa().unwrap();
      showSuccess('Two-factor authentication has been disabled');
      resetFlow();
    } catch (err) {
      const message =
        (err as { data?: { error?: string } })?.data?.error ??
        'Could not disable two-factor authentication. Please try again.';
      showError(message);
    }
  };

  const handleCopySecret = async () => {
    if (!setupData) return;
    try {
      await navigator.clipboard.writeText(setupData.secret);
      showSuccess('Secret copied to clipboard');
    } catch {
      // Clipboard API can be unavailable (older browsers, insecure context).
      // The secret is already selectable/copyable text, so this is a
      // non-fatal convenience failure.
    }
  };

  const enabled = mfaStatus?.enabled ?? false;

  return (
    <div className="bg-white shadow rounded-lg mt-6" data-testid="mfa-panel">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Two-Factor Authentication
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Add an extra layer of security to your account using an authenticator app.
            </p>
          </div>
          {!isStatusLoading && (
            <span
              data-testid="mfa-status-badge"
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {enabled ? (
                <ShieldCheckIcon className="h-3.5 w-3.5" />
              ) : (
                <ShieldExclamationIcon className="h-3.5 w-3.5" />
              )}
              {enabled ? 'Enabled' : 'Not enabled'}
            </span>
          )}
        </div>

        {/* Off-state, idle: show the enable entry point */}
        {!enabled && stage === 'idle' && (
          <button
            type="button"
            onClick={handleEnableClick}
            data-testid="mfa-enable-btn"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Enable Two-Factor Authentication
          </button>
        )}

        {/* Off-state, password step */}
        {!enabled && stage === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Confirm your password to enable two-factor authentication
              </label>
              <input
                type="password"
                autoComplete="current-password"
                data-testid="mfa-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetFlow}
                data-testid="mfa-password-cancel"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isStarting}
                data-testid="mfa-password-submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isStarting ? 'Verifying…' : 'Continue'}
              </button>
            </div>
          </form>
        )}

        {/* Off-state, confirm step: show secret + code entry */}
        {!enabled && stage === 'confirm' && setupData && (
          <form onSubmit={handleConfirmSubmit} className="space-y-4">
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Scan this QR code with your authenticator app, or enter the key manually:
              </p>
              {/* Scannable QR — the primary path on desktop, where tapping an
                  otpauth:// link does nothing useful. Rendered client-side onto
                  the canvas; the secret never leaves the browser. */}
              <div className="flex justify-center mb-3">
                <canvas
                  ref={qrCanvasRef}
                  data-testid="mfa-setup-qr"
                  aria-label="Two-factor authentication QR code"
                  className="rounded-md border border-gray-200 bg-white p-2"
                />
              </div>
              <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-1">Or enter this key manually:</p>
                <div className="flex items-center justify-between gap-2">
                  <code
                    data-testid="mfa-setup-secret"
                    className="text-sm font-mono text-gray-900 break-all select-all"
                  >
                    {setupData.secret}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopySecret}
                    data-testid="mfa-copy-secret-btn"
                    className="flex-shrink-0 text-xs font-medium text-primary-600 hover:text-primary-900"
                  >
                    Copy
                  </button>
                </div>
                {/* Kept for phones: tapping this opens the authenticator app
                    directly. On desktop it's a no-op, which is why the QR above
                    is the primary path there. */}
                <a
                  href={setupData.otpauthUri}
                  data-testid="mfa-setup-otpauth-link"
                  className="mt-2 block text-xs text-primary-600 hover:text-primary-900 break-all"
                >
                  {setupData.otpauthUri}
                </a>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Enter the 6-digit code from your authenticator app
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                data-testid="mfa-confirm-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetFlow}
                data-testid="mfa-confirm-cancel"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isVerifying || code.length !== 6}
                data-testid="mfa-confirm-submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isVerifying ? 'Confirming…' : 'Confirm & Enable'}
              </button>
            </div>
          </form>
        )}

        {/* On-state */}
        {enabled && stage === 'idle' && (
          <div>
            <p className="text-sm text-gray-500 mb-4">
              Two-factor authentication is protecting your account.
            </p>
            <button
              type="button"
              onClick={handleDisableClick}
              data-testid="mfa-disable-btn"
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              Turn Off Two-Factor Authentication
            </button>
          </div>
        )}

        {enabled && stage === 'disable-confirm' && (
          <div className="rounded-md bg-red-50 border border-red-200 p-4">
            <p className="text-sm text-red-800 mb-4">
              Are you sure you want to turn off two-factor authentication? This will make your
              account less secure.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetFlow}
                data-testid="mfa-disable-cancel"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDisableConfirm}
                disabled={isDisabling}
                data-testid="mfa-disable-confirm"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {isDisabling ? 'Disabling…' : 'Turn Off'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MfaPanel;
